import * as zExt from "@redotech/rpc/ext";
import * as z from "zod";

// Gorgias View Section (maps to SupportFolder)
const GorgiasViewSectionSchema = z.object({
  id: z.number(),
  name: z.string(),
  private: z.boolean(),
  decoration: z.object({ emoji: z.string() }).nullable(),
  created_datetime: z.string(),
  updated_datetime: z.string(),
  uri: z.string().optional(),
});
export type GorgiasViewSection = z.infer<typeof GorgiasViewSectionSchema>;

const GorgiasViewSectionsResponseSchema = z.object({
  object: z.literal("list"),
  uri: z.string().optional(),
  data: z.array(GorgiasViewSectionSchema),
  meta: z
    .object({
      prev_cursor: z.string().nullable(),
      next_cursor: z.string().nullable(),
    })
    .optional(),
});
export type GorgiasViewSectionsResponse = z.infer<
  typeof GorgiasViewSectionsResponseSchema
>;

// Gorgias AST types for filter parsing - using loose schema since AST is complex and recursive
const GorgiasAstLocationSchema = z.object({
  line: z.number(),
  column: z.number(),
});

const GorgiasAstLocSchema = z.object({
  start: GorgiasAstLocationSchema,
  end: GorgiasAstLocationSchema,
});

// Base AST node - we use a recursive lazy schema
const GorgiasAstNodeSchema: z.ZodType<GorgiasAstNode> = z.lazy(() =>
  z.object({
    type: z.string(),
    loc: GorgiasAstLocSchema.optional(),
    name: z.string().optional(),
    value: z.union([z.string(), z.number(), z.boolean()]).optional(),
    raw: z.string().optional(),
    operator: z.string().optional(),
    callee: GorgiasAstNodeSchema.optional(),
    object: GorgiasAstNodeSchema.optional(),
    property: GorgiasAstNodeSchema.optional(),
    computed: z.boolean().optional(),
    left: GorgiasAstNodeSchema.optional(),
    right: GorgiasAstNodeSchema.optional(),
    expression: GorgiasAstNodeSchema.optional(),
    arguments: z.array(GorgiasAstNodeSchema).optional(),
    elements: z.array(GorgiasAstNodeSchema).optional(),
    body: z.array(GorgiasAstNodeSchema).optional(),
    sourceType: z.string().optional(),
  }),
);

export interface GorgiasAstNode {
  type: string;
  loc?: {
    start: { line: number; column: number };
    end: { line: number; column: number };
  };
  name?: string;
  value?: string | number | boolean;
  raw?: string;
  operator?: string;
  callee?: GorgiasAstNode;
  object?: GorgiasAstNode;
  property?: GorgiasAstNode;
  computed?: boolean;
  left?: GorgiasAstNode;
  right?: GorgiasAstNode;
  expression?: GorgiasAstNode;
  arguments?: GorgiasAstNode[];
  elements?: GorgiasAstNode[];
  body?: GorgiasAstNode[];
  sourceType?: string;
}

// Gorgias View
const GorgiasViewSchema = z.object({
  id: z.number(),
  category: z.string().nullable(),
  name: z.string(),
  filters: z.string(),
  filters_ast: GorgiasAstNodeSchema.nullable(),
  fields: z.array(z.string()).optional(),
  order_by: z.string().optional(),
  order_dir: z.enum(["asc", "desc"]).optional(),
  search: z.string().nullable().optional(),
  section_id: z.number().nullable(),
  slug: z.string().optional(),
  type: z.string().optional(),
  visibility: z.string().optional(),
  decoration: z.object({ emoji: z.string() }).nullable().optional(),
  created_datetime: z.string(),
  deactivated_datetime: z.string().nullable(),
  uri: z.string().optional(),
  shared_with_teams: z.array(z.unknown()).optional(),
  shared_with_users: z.array(z.unknown()).optional(),
});
export type GorgiasView = z.infer<typeof GorgiasViewSchema>;

const GorgiasViewsResponseSchema = z.object({
  object: z.literal("list"),
  uri: z.string().optional(),
  data: z.array(GorgiasViewSchema),
  meta: z
    .object({
      prev_cursor: z.string().nullable(),
      next_cursor: z.string().nullable(),
    })
    .optional(),
});
export type GorgiasViewsResponse = z.infer<typeof GorgiasViewsResponseSchema>;

// RPC endpoint definition
export const importGorgiasViews = {
  name: "importGorgiasViews" as const,
  input: z.object({
    teamId: zExt.objectId(),
    gorgiasViewSectionsJson: GorgiasViewSectionsResponseSchema,
    gorgiasViewsJson: GorgiasViewsResponseSchema,
  }),
  output: z.object({
    success: z.boolean(),
    foldersCreated: z.number(),
    foldersSkipped: z.number(),
    viewsCreated: z.number(),
    viewsSkipped: z.number(),
    warnings: z.array(z.string()),
    errors: z.array(z.string()),
  }),
};
